import { Textarea } from "@qualtio-workhub/ui";
import {
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type ActiveMentionFragment,
  type AutocompleteUser,
  filterUsersForAutocomplete,
  getActiveMentionFragment,
  getUserMentionToken,
  replaceActiveMentionFragment,
} from "./user-autocomplete";

type MentionTextareaProps = {
  ariaLabel: string;
  placeholder: string;
  users: readonly AutocompleteUser[];
  value: string;
  noResultsLabel: string;
  onValueChange(value: string): void;
};

export function MentionTextarea({
  ariaLabel,
  placeholder,
  users,
  value,
  noResultsLabel,
  onValueChange,
}: MentionTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const generatedId = useId();
  const listboxId = `mention-${generatedId}-listbox`;
  const [fragment, setFragment] = useState<ActiveMentionFragment | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const suggestions = useMemo(
    () => filterUsersForAutocomplete(users, fragment?.query ?? "").slice(0, 8),
    [fragment?.query, users],
  );
  const open = fragment !== null;
  const activeOptionId =
    open && suggestions[activeIndex] ? getMentionOptionId(generatedId, activeIndex) : undefined;

  function refreshFragment(nextValue: string, cursor: number) {
    setFragment(getActiveMentionFragment(nextValue, cursor));
    setActiveIndex(0);
  }

  function selectUser(user: AutocompleteUser) {
    const textarea = textareaRef.current;
    const cursor = textarea?.selectionStart ?? value.length;
    const replacement = replaceActiveMentionFragment(value, cursor, getUserMentionToken(user));
    onValueChange(replacement.value);
    setFragment(null);
    setActiveIndex(0);
    window.setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(replacement.cursor, replacement.cursor);
    }, 0);
  }

  function onChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const nextValue = event.currentTarget.value;
    onValueChange(nextValue);
    refreshFragment(nextValue, event.currentTarget.selectionStart);
  }

  return (
    <div className="mention-input" data-mention-input="true">
      <Textarea
        aria-activedescendant={activeOptionId}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-label={ariaLabel}
        data-mention-input="true"
        placeholder={placeholder}
        ref={textareaRef}
        role="combobox"
        value={value}
        onBlur={() => window.setTimeout(() => setFragment(null), 100)}
        onChange={onChange}
        onClick={(event) => refreshFragment(value, event.currentTarget.selectionStart)}
        onKeyDown={(event) =>
          handleMentionKeyDown(event, suggestions, activeIndex, setActiveIndex, selectUser, () =>
            setFragment(null),
          )
        }
        onKeyUp={(event) => refreshFragment(value, event.currentTarget.selectionStart)}
      />
      {open ? (
        <MentionSuggestionList
          activeIndex={activeIndex}
          emptyLabel={noResultsLabel}
          generatedId={generatedId}
          id={listboxId}
          suggestions={suggestions}
          onSelect={selectUser}
        />
      ) : null}
    </div>
  );
}

function handleMentionKeyDown(
  event: KeyboardEvent<HTMLTextAreaElement>,
  suggestions: readonly AutocompleteUser[],
  activeIndex: number,
  setActiveIndex: (index: number) => void,
  selectUser: (user: AutocompleteUser) => void,
  close: () => void,
) {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    setActiveIndex(nextIndex(activeIndex, suggestions.length, 1));
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    setActiveIndex(nextIndex(activeIndex, suggestions.length, -1));
  } else if (event.key === "Enter" && suggestions[activeIndex] !== undefined) {
    event.preventDefault();
    selectUser(suggestions[activeIndex]);
  } else if (event.key === "Escape") {
    event.preventDefault();
    close();
  }
}

function MentionSuggestionList({
  activeIndex,
  emptyLabel,
  generatedId,
  id,
  suggestions,
  onSelect,
}: {
  activeIndex: number;
  emptyLabel: string;
  generatedId: string;
  id: string;
  suggestions: readonly AutocompleteUser[];
  onSelect(user: AutocompleteUser): void;
}) {
  return (
    <div className="user-autocomplete-list mention-suggestion-list" id={id} role="listbox">
      {suggestions.length === 0 ? (
        <div className="user-autocomplete-empty">{emptyLabel}</div>
      ) : null}
      {suggestions.map((user, index) => (
        <button
          aria-selected={index === activeIndex}
          className="user-autocomplete-option"
          id={getMentionOptionId(generatedId, index)}
          key={user.id}
          role="option"
          type="button"
          onMouseDown={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
          onClick={() => onSelect(user)}
        >
          <strong>{getUserMentionToken(user)}</strong>
          <span>{user.name}</span>
        </button>
      ))}
    </div>
  );
}

function getMentionOptionId(generatedId: string, index: number): string {
  return `mention-${generatedId}-option-${index}`;
}

function nextIndex(current: number, length: number, direction: 1 | -1): number {
  if (length === 0) return 0;
  return (current + direction + length) % length;
}
